import React, { useEffect, useRef, useState } from 'react';

import {
  AssistFeedbackChoice,
  AssistFeedbackRequest,
} from './WritingAssistMoments.types';
import {
  FeedbackActions,
  FeedbackPanel,
} from './WritingAssistMoments.styles';

export interface AssistFeedbackProps {
  assistReference?: string;
  submitFeedback: (request: AssistFeedbackRequest) => Promise<unknown>;
}

const REFERENCE_PATTERN = /^[A-Za-z0-9_-]{43}$/;

const AssistFeedback: React.FC<AssistFeedbackProps> = ({ assistReference, submitFeedback }) => {
  const [pending, setPending] = useState(false);
  const [selected, setSelected] = useState<AssistFeedbackChoice | null>(null);
  const currentReference = useRef(assistReference);
  currentReference.current = assistReference;

  useEffect(() => {
    setPending(false);
    setSelected(null);
  }, [assistReference]);

  if (!assistReference || !REFERENCE_PATTERN.test(assistReference)) return null;

  const choose = async (feedback: AssistFeedbackChoice) => {
    if (pending || selected) return;
    const submittedReference = assistReference;
    setPending(true);
    try {
      await submitFeedback({ assistReference: submittedReference, feedback });
      if (currentReference.current === submittedReference) setSelected(feedback);
    } catch (_) {
      // The writing save already succeeded. Leave the prompt retryable and do
      // not replace deck-approved copy with a new failure message.
    } finally {
      if (currentReference.current === submittedReference) setPending(false);
    }
  };

  return (
    <FeedbackPanel aria-live="polite">
      <p>Saved. How were the suggestions?</p>
      <FeedbackActions>
        <button
          type="button"
          aria-label="Thumbs up"
          aria-pressed={selected === 'up'}
          disabled={pending || selected !== null}
          onClick={() => choose('up')}
        >
          👍
        </button>
        <button
          type="button"
          aria-label="Thumbs down"
          aria-pressed={selected === 'down'}
          disabled={pending || selected !== null}
          onClick={() => choose('down')}
        >
          👎
        </button>
      </FeedbackActions>
    </FeedbackPanel>
  );
};

export default AssistFeedback;
